/**
 * Pan/zoom geometry for cropping an image to a fixed-shape frame, kept free of
 * the DOM so the arithmetic that decides what ends up in the crop can be
 * tested directly. Shared by Events and Ad Spaces.
 */

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 4;

export type Size = { width: number; height: number };
export type Point = { x: number; y: number };

/** Position and zoom of the image behind the frame. */
export type CropTransform = { zoom: number; offset: Point };

export const INITIAL_TRANSFORM: CropTransform = {
  zoom: MIN_ZOOM,
  offset: { x: 0, y: 0 },
};

/**
 * The scale at which the image exactly covers the frame. Everything else is a
 * multiple of this, which is what makes zoom 1 the floor: below it the frame
 * would show empty bars.
 */
export function coverScale(natural: Size, frame: Size): number {
  if (natural.width <= 0 || natural.height <= 0) return 1;
  return Math.max(frame.width / natural.width, frame.height / natural.height);
}

export function scaleFor(natural: Size, frame: Size, zoom: number): number {
  return coverScale(natural, frame) * clampZoom(zoom);
}

export function clampZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return MIN_ZOOM;
  return Math.min(Math.max(zoom, MIN_ZOOM), MAX_ZOOM);
}

/**
 * How far the image may be dragged before an edge would come inside the frame.
 * At zoom 1 on the axis that already fits exactly, this is 0 — that axis
 * cannot move at all.
 */
export function offsetBounds(natural: Size, frame: Size, zoom: number): Point {
  const scale = scaleFor(natural, frame, zoom);
  return {
    x: Math.max(0, (natural.width * scale - frame.width) / 2),
    y: Math.max(0, (natural.height * scale - frame.height) / 2),
  };
}

export function clampOffset(
  offset: Point,
  natural: Size,
  frame: Size,
  zoom: number,
): Point {
  const bounds = offsetBounds(natural, frame, zoom);
  return {
    x: Math.min(Math.max(offset.x, -bounds.x), bounds.x),
    y: Math.min(Math.max(offset.y, -bounds.y), bounds.y),
  };
}

/**
 * Zooming keeps the frame's centre over the same part of the image, then
 * re-clamps — otherwise zooming out at an edge would expose a bar.
 */
export function applyZoom(
  transform: CropTransform,
  nextZoom: number,
  natural: Size,
  frame: Size,
): CropTransform {
  const zoom = clampZoom(nextZoom);
  const ratio =
    scaleFor(natural, frame, zoom) / scaleFor(natural, frame, transform.zoom);
  const scaled = {
    x: transform.offset.x * ratio,
    y: transform.offset.y * ratio,
  };
  return { zoom, offset: clampOffset(scaled, natural, frame, zoom) };
}

export function applyPan(
  transform: CropTransform,
  delta: Point,
  natural: Size,
  frame: Size,
): CropTransform {
  return {
    zoom: transform.zoom,
    offset: clampOffset(
      { x: transform.offset.x + delta.x, y: transform.offset.y + delta.y },
      natural,
      frame,
      transform.zoom,
    ),
  };
}

export type CropRect = { x: number; y: number; width: number; height: number };

/**
 * The part of the original image inside the frame, in the original's own
 * pixels — which is what the export reads from, so it must not depend on how
 * large the stage happened to be on screen.
 */
export function cropRect(
  transform: CropTransform,
  natural: Size,
  frame: Size,
): CropRect {
  const scale = scaleFor(natural, frame, transform.zoom);
  const width = frame.width / scale;
  const height = frame.height / scale;
  const centreX = natural.width / 2 - transform.offset.x / scale;
  const centreY = natural.height / 2 - transform.offset.y / scale;
  return {
    x: centreX - width / 2,
    y: centreY - height / 2,
    width,
    height,
  };
}
